import React from 'react';
import { Link } from 'react-router-dom';

/**
 * Error Boundary Component
 * Catches JavaScript errors anywhere in child component tree
 * Displays a fallback UI instead of crashing the whole app
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render shows fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log error to console in development
    console.error('Error Boundary caught an error:', error, errorInfo);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });

    // Here you could also log to an error reporting service
    // logErrorToService(error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#0a0a0d',
          padding: '2rem'
        }}>
          <div style={{
            textAlign: 'center',
            maxWidth: '500px'
          }}>
            {/* Error icon */}
            <div style={{
              fontSize: '4rem',
              marginBottom: '1.5rem',
              color: '#D9CBA0'
            }}>
              ⚠️
            </div>
            
            <h1 style={{
              color: '#E8D8A8',
              fontSize: '1.75rem',
              fontWeight: '300',
              marginBottom: '1rem',
              letterSpacing: '0.05em'
            }}>
              Something went wrong
            </h1>
            
            <p style={{
              color: 'rgba(255, 255, 255, 0.6)',
              fontSize: '1rem',
              lineHeight: '1.6',
              marginBottom: '2rem'
            }}>
              We encountered an unexpected error. Please try refreshing the page or return to the homepage.
            </p>
            
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              flexWrap: 'wrap'
            }}>
              <button
                onClick={this.handleRetry}
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'linear-gradient(135deg, #D9CBA0 0%, #C4A87C 100%)',
                  color: '#0a0a0d',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em'
                }}
              >
                Try Again
              </button>
              
              <a
                href="/"
                style={{
                  padding: '0.75rem 1.5rem',
                  background: 'transparent',
                  color: '#D9CBA0',
                  border: '1px solid rgba(217, 203, 160, 0.4)',
                  borderRadius: '4px',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  textDecoration: 'none'
                }}
              >
                Go Home
              </a>
            </div>
            
            {/* Show error details in development */}
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details style={{
                marginTop: '2rem',
                textAlign: 'left',
                background: 'rgba(255, 0, 0, 0.1)',
                padding: '1rem',
                borderRadius: '4px',
                border: '1px solid rgba(255, 0, 0, 0.2)'
              }}>
                <summary style={{
                  color: '#ff6b6b',
                  cursor: 'pointer',
                  marginBottom: '0.5rem'
                }}>
                  Error Details (Development Only)
                </summary>
                <pre style={{
                  color: 'rgba(255, 255, 255, 0.7)',
                  fontSize: '0.75rem',
                  overflow: 'auto',
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word'
                }}>
                  {this.state.error && this.state.error.toString()}
                  {this.state.errorInfo && this.state.errorInfo.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
